import type { ReactNode, SubmitEvent } from "react";
import { Modal } from "./modal";
import { Button } from "./button";
import { Icon, Lock, ArrowRight } from "./icon";
import { Field } from "./field";
import { Input } from "./input";
import type { QuotaLimit } from "@/lib/billing/quota";
import { paywallCopyForQuotaLimit } from "@/lib/billing/paywallCopy";

/**
 * Soft-block upsell modal (M7 FR-Q5). When a quota gate blocks an action, this
 * NAMES the exact limit hit, shows the transparent on-screen price, and offers a
 * SINGLE Upgrade CTA that goes straight to checkout for that benefit. No generic
 * error, no silent fail, and — dark-pattern-free — a plain, always-visible link
 * to the full pricing page and a normal close. Token-only; brand name + price
 * arrive as props from the white-label config, never as literals.
 */

export interface UpsellPrice {
  monthlyCents: number | null;
  annualCents: number | null;
  currency: string;
}

export interface UpsellModalProps {
  open: boolean;
  onClose: () => void;
  /** The specific limit the block is attributable to (from quota.check). */
  limit: QuotaLimit;
  /** White-label brand name — from getTheme, never a literal. */
  brandName: string;
  /** White-label brand mark (brand.logoUrl); shown in the glyph, falls back to a lock. */
  logoUrl?: string;
  /** Transparent price from the tunable plan table; null hides the price line. */
  price: UpsellPrice | null;
  /** Display name of the recommended plan resolved from the plan table. */
  planName?: string;
  /** Configured per-map location allowance for the recommended plan. */
  planMapPointLimit?: number | null;
  /** Fires the checkout redirect for this benefit. */
  onUpgrade: () => void;
  /** Checkout redirect is in flight. */
  upgrading?: boolean;
  /** Full pricing page (default "/pricing"). */
  pricingHref?: string;
  /**
   * Optional copy emphasis rendered above the CTA — the seam the M12 paywall-copy
   * experiment uses to render its variant. Presentational only; never a dark
   * pattern (no fake scarcity, no countdown), and the cancel path is untouched.
   */
  emphasis?: ReactNode;
  /** Anonymous-only identity capture before auth + checkout. Never marketing consent. */
  emailCapture?: {
    value: string;
    onChange: (value: string) => void;
    error?: string | null;
    signInHref?: string;
  };
}

function formatPrice(cents: number, currency: string): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100);
}

export function PriceLine({ price }: { price: UpsellPrice }) {
  if (price.monthlyCents === null) return null;
  return (
    <p className="pz-upsell__price">
      <span className="pz-upsell__amountrow">
        <span className="pz-upsell__amount">{formatPrice(price.monthlyCents, price.currency)}</span>
        <span className="pz-upsell__per">/month</span>
      </span>
    </p>
  );
}

export function UpsellModal({
  open,
  onClose,
  limit,
  brandName,
  logoUrl,
  price,
  planName,
  planMapPointLimit,
  onUpgrade,
  upgrading = false,
  pricingHref = "/pricing",
  emphasis,
  emailCapture,
}: UpsellModalProps) {
  const copy = paywallCopyForQuotaLimit(limit, brandName);

  function handleSubmit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    onUpgrade();
  }

  // MAP-117 (D2): visual-only restyle — `pz-promodal` layers the same
  // gradient-hero header used by the generic ProUpsellModal onto this
  // instance (see primitives.css). Props, shared copy-catalog logic, and the canonical
  // paywall_hit/upgrade_clicked emissions are fired by the wrapping Paywall;
  // checkout_started is server-confirmed after Stripe creates a session.
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        planName ? (
          <span className="pz-promodal__eyebrow">
            <span className="pz-promodal__eyebrow-badge">{planName.toUpperCase()}</span>
          </span>
        ) : (
          "Upgrade to continue"
        )
      }
      className="pz-upsell pz-promodal"
    >
      <form className="pz-upsell__body" onSubmit={handleSubmit}>
        <span className="pz-upsell__glyph" aria-hidden>
          {logoUrl ? (
            // eslint-disable-next-line @next/next/no-img-element -- config-driven brand mark
            <img className="pz-upsell__glyphlogo" src={logoUrl} alt="" />
          ) : (
            <Icon icon={Lock} size="lg" />
          )}
        </span>
        <h3 className="pz-upsell__heading">{copy.heading}</h3>
        <p className="pz-upsell__copy">{copy.body}</p>
        {emphasis ? <p className="pz-upsell__emphasis">{emphasis}</p> : null}
        {planName || price ? (
          <section className="pz-upsell__plan" aria-label="Required plan">
            <div className="pz-upsell__planidentity">
              <span className="pz-upsell__planlabel">Requires</span>
              {planName ? <strong className="pz-upsell__planname">{planName}</strong> : null}
            </div>
            <p className="pz-upsell__planbenefit">
              {limit === "map_points" && planMapPointLimit !== undefined
                ? planMapPointLimit === null
                  ? "Map unlimited locations per map."
                  : `Map up to ${planMapPointLimit.toLocaleString()} locations per map`
                : copy.benefit}
            </p>
            {price ? <PriceLine price={price} /> : null}
          </section>
        ) : (
          <p className="pz-upsell__planbenefit">{copy.benefit}</p>
        )}
        {emailCapture ? (
          <div className="pz-promodal__actionpanel pz-upsell__actionpanel">
            <Field
              className="pz-upsell__email"
              label="Enter your email to save this map"
              htmlFor="paywall-email"
              required
              error={emailCapture.error}
              description="We won't email you without your permission."
            >
              <Input
                id="paywall-email"
                name="email"
                type="email"
                autoComplete="email"
                required
                maxLength={320}
                value={emailCapture.value}
                error={Boolean(emailCapture.error)}
                onChange={(event) => emailCapture.onChange(event.target.value)}
              />
            </Field>
            <Button
              type="submit"
              variant="upgrade"
              size="lg"
              loading={upgrading}
              iconTrailing={upgrading ? undefined : <Icon icon={ArrowRight} size="sm" />}
            >
              Save Map
            </Button>
          </div>
        ) : (
          <Button
            type="submit"
            variant="upgrade"
            size="lg"
            loading={upgrading}
            className="pz-upsell__cta"
            iconTrailing={upgrading ? undefined : <Icon icon={ArrowRight} size="sm" />}
          >
            Upgrade now
          </Button>
        )}
        {emailCapture?.signInHref ? (
          <a className="pz-upsell__compare" href={emailCapture.signInHref}>
            Already have an account? Sign in
          </a>
        ) : null}
        <a className="pz-upsell__compare" href={pricingHref}>
          Compare all plans
        </a>
        {!emailCapture ? <p className="pz-upsell__terms">Cancel anytime. Your maps remain available.</p> : null}
      </form>
    </Modal>
  );
}
